/**
 * API Configuration - Centralized axios instance
 * All API calls should use this client for consistent behavior
 */
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token and logging
apiClient.interceptors.request.use(
  (config) => {
    // Get token from Supabase session
    const token = localStorage.getItem('supabase.auth.token');
    if (token) {
      try {
        const parsed = JSON.parse(token);
        if (parsed?.access_token) {
          config.headers.Authorization = `Bearer ${parsed.access_token}`;
        }
      } catch {
        // Token might be plain string
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Log requests in development
    if (import.meta.env.DEV) {
      console.log(`🌐 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors globally
apiClient.interceptors.response.use(
  (response) => {
    // Log responses in development
    if (import.meta.env.DEV) {
      console.log(`✅ API Response: ${response.config.url} (${response.status})`);
    }
    return response;
  },
  (error) => {
    const { response } = error;

    // Handle specific error codes
    if (response?.status === 401) {
      console.warn('🔒 Unauthorized - triggering logout');
      window.dispatchEvent(new CustomEvent('auth:logout'));
    } else if (response?.status === 403) {
      console.warn('🚫 Forbidden - access denied');
    } else if (response?.status === 404) {
      console.warn('🔍 Not found:', response.config.url);
    } else if (response?.status >= 500) {
      console.error('💥 Server error:', response.status, response.data);
    } else if (!response) {
      console.error('🔌 Network error - no response received');
    }

    return Promise.reject(error);
  }
);

/**
 * Helper to handle API errors consistently
 */
export const handleApiError = (error, defaultMessage = 'An error occurred') => {
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return defaultMessage;
};

export default apiClient;
